/**
 * <ProfileSwitcherSheet> — « Changer de profil » (molecule).
 *
 * La feuille que la pastille du header ouvre : l'espace parent en tête, puis un profil par
 * enfant de la famille. Port fidèle de `ProfileSwitcherM.js:346-398` (la liste défilante, la
 * ligne parent, les lignes de comptes, la mention « Actif », le lien « Mon compte » sur le
 * profil en cours et le loader sur celui qui bascule).
 *
 * Trois différences assumées avec le legacy : la coque est le `<Modal>` du DS (même portail,
 * même voile, même retour arrière que les autres feuilles, au lieu d'un `Animated` maison) ;
 * l'ordre des profils est celui que l'appelant donne, là où le legacy remontait l'actif en tête
 * et faisait sauter la liste d'une bascule à l'autre ; et le violet de la ligne parent devient
 * la couleur de l'univers — en V13, l'espace parent est marine, il n'y a plus deux teintes.
 *
 * Contrôlée de bout en bout : visibilité, profil sélectionné et bascule en cours arrivent en
 * props, les gestes remontent en callbacks.
 *
 * En pied de feuille, « Se déconnecter » et la porte de sortie : le geste déconnecte et ramène à
 * « Qui utilise Masteur ? » — c'est ce que le parent voit arriver, là où « Changer de compte »
 * (le libellé du bouton de l'espace élève, `LogoutButton.js`) promettait un autre compte. La
 * feuille ne sait rien de la déconnexion : elle montre le bouton quand l'appelant donne le geste,
 * le fait tourner pendant qu'il travaille, et le bloque pendant la bascule vers un enfant.
 *
 * Entre la liste et le pied, la section « Apparence » (Automatique · Clair · Sombre), quand
 * l'appelant donne `appearance` : le choix s'applique tout de suite et la feuille reste ouverte.
 *
 * Depuis l'espace élève, c'est la feuille « moi » (`me`, Wajih 16/10) : l'avatar de l'élève dans
 * l'en-tête l'ouvre, et sa TÊTE est l'élève lui-même — une grande ligne avec son avatar signé, son
 * prénom, son titre et « Mon compte › » (`onOpenAccountSettings`), la seule porte vers son compte.
 * Dessous, « Changer de profil » : les autres comptes élèves de la famille, puis « Espace parent »,
 * qui se choisit (`parentActive={false}` + `onSelectParent`) ; changer de profil reste à un geste.
 * Sans famille connue sur l'appareil (`profiles` vide), pas de liste : la tête, « Apparence » et
 * « Se déconnecter ». Ce sont les lignes, la section et le pied du parent, par les mêmes props :
 * `me` ajoute la tête, rien d'autre ne change de forme. L'élève comme le parent s'ouvrent en
 * Automatique (Wajih, 19/10) : la valeur par défaut est celle de l'appelant, pas de la feuille.
 *
 * Depuis l'espace masteur, la même feuille « moi », et elle EST son compte (Wajih, 29/10 : la page
 * « Mon compte » doublait la feuille, elle est retirée). Sous la tête, en lignes de réglage, tout
 * ce que la page portait (`links` : « Valide ton adresse email », « Ma dispo », « Mes factures »,
 * « Suivre mes paiements », qui sort de l'app (`external`), « Historique des lives », « Changer ma
 * photo », « Paramètres »), et sous « Se déconnecter » la version de l'app (`version`). Sa tête porte
 * la capsule (`meAvatarVariant="tutor"`) et, sous « Membre depuis … », son fuseau quand il diffère de
 * celui du serveur (`me.notes`). « Suivre mes paiements » et « Changer ma photo » attendent une
 * réponse : la feuille reste ouverte et la ligne tourne (`keepOpen`), comme l'ancienne page montrait
 * « Redirection… ». Le masteur s'ouvre en Clair (Wajih, 28/10).
 */
import React from "react"
import { Pressable, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Avatar } from "../../atoms/Avatar"
import { Button } from "../../atoms/Buttons/Button"
import { Icon, type IconName } from "../../atoms/icons/Icon"
import { Loader } from "../../atoms/Loader"
import { Modal } from "../Modal"
import { SettingRow } from "../SettingRow"
import { AppearanceSection, type AppearanceChoice } from "./AppearanceSection"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { SectionLabel } from "../../atoms/typography/SectionLabel"
import { Text } from "../../atoms/typography/Text"
import { type UIParentChild, type UIProfileSwitcherMe } from "../../../ports/primary/ui/types"

export type ProfileSwitcherSheetProps = {
    visible: boolean
    onClose: () => void
    /** Les profils élèves de la famille, dans l'ordre d'affichage. */
    profiles?: UIParentChild[]
    /** Profil actuellement servi par l'espace parent — il porte « Actif ». */
    selectedProfileId?: string | number | null
    /** Profil dont la bascule est en cours : sa ligne montre un loader, les autres s'estompent. */
    switchingProfileId?: string | number | null
    /** Basculer vers l'espace élève de ce profil (legacy `onAccountSelect` → `handleExitToChild`). */
    onSelectProfile?: (profile: UIParentChild) => void
    /** « Mon compte », sous le profil actif (ou la tête, avec `me`). Absent → pas de lien. */
    onOpenAccountSettings?: () => void
    /** Image empaquetée de l'espace parent (import PNG). */
    parentAvatarSource?: number
    /** Légende de la ligne parent — défaut « Espace parent ». */
    parentLabel?: string
    /** « Se déconnecter », en pied de feuille. Absent → pas de bouton. */
    onSwitchAccount?: () => void
    /** La déconnexion est en cours : le bouton tourne, les profils ne se choisissent plus. */
    switchingAccount?: boolean
    /** La section « Apparence » : le choix courant et son geste. Absent → pas de section. */
    appearance?: { value: AppearanceChoice; onChange: (value: AppearanceChoice) => void }
    /**
     * L'espace parent est le profil courant — défaut `true` : sa ligne est en tête, marquée
     * « Actif », inerte. `false` depuis l'espace élève : la ligne passe en dernier et se choisit.
     */
    parentActive?: boolean
    /**
     * Entrer dans l'espace parent, depuis l'espace élève. Sans effet tant que `parentActive`.
     * Absente depuis l'espace élève (`parentActive={false}`) : la ligne n'existe pas, il n'y a pas
     * d'espace parent où aller.
     */
    onSelectParent?: () => void
    /**
     * La feuille « moi » de l'élève : il en est la tête, et la liste des profils passe sous
     * « Changer de profil », sans lui. Absent → la feuille du parent, inchangée.
     */
    me?: UIProfileSwitcherMe
    /**
     * Des portes sous la tête « moi », en lignes de réglage (`<SettingRow>`) : celles de l'ancien
     * « Mon compte » du masteur. La feuille se ferme, puis le geste part — sauf pour une porte
     * `keepOpen`, dont l'attente se lit dans la feuille ; une ligne qui attend (`loading`) tourne et
     * ne répond plus. Aucune par défaut.
     */
    links?: ProfileSheetLink[]
    /**
     * L'avatar de la tête « moi ». Défaut : l'avatar signé de l'élève ; `tutor` : la capsule du
     * masteur (`<Avatar variant="tutor">` — un tuteur porte toujours la capsule).
     */
    meAvatarVariant?: "signed" | "tutor"
    /** « 3.9.2 » : la ligne « Version … » sous « Se déconnecter ». Absent (défaut) : pas de ligne. */
    version?: string
    testID?: string
}

/** Une porte de la feuille « moi » : son mot, son glyphe, son geste. */
export type ProfileSheetLink = {
    key: string
    label: string
    iconName: IconName
    onPress: () => void
    /** Le geste attend une réponse : la ligne tourne et ne répond plus. */
    loading?: boolean
    /** Le geste ouvre une page web, hors de l'app : le glyphe « lien externe » à la place du chevron. */
    external?: boolean
    /**
     * Le geste attend une réponse que la ligne montre (`loading`) : la feuille reste ouverte, sinon
     * l'attente tournerait dans une feuille fermée — « Suivre mes paiements » demande l'adresse du
     * tableau Stripe, « Changer ma photo » envoie la photo. Défaut `false` : la feuille se ferme, puis
     * le geste part.
     */
    keepOpen?: boolean
}

const NO_NOTES: string[] = []

/** Taille de l'avatar d'une ligne — le 44 de la table du livre. */
const ROW_AVATAR_SIZE = 44
/** Taille de l'avatar signé de la tête « moi » — le 54 de la table du livre. */
const ME_AVATAR_SIZE = 54

export function ProfileSwitcherSheet(props: ProfileSwitcherSheetProps) {
    const {
        visible,
        onClose,
        profiles = [],
        selectedProfileId = null,
        switchingProfileId = null,
        onSelectProfile,
        onOpenAccountSettings,
        parentAvatarSource,
        parentLabel = "Espace parent",
        onSwitchAccount,
        switchingAccount = false,
        appearance,
        parentActive = true,
        onSelectParent,
        me,
        links = [],
        meAvatarVariant = "signed",
        version,
        testID,
    } = props

    const switching = switchingProfileId != null
    // Une ligne qu'on taperait pendant la déconnexion ferait rejoindre un enfant sur un compte qui
    // se ferme : pendant l'une ou l'autre bascule, aucun profil ne se choisit.
    const busy = switching || switchingAccount

    // Dans l'espace parent, la ligne parent est le profil courant : elle se montre en tête et ne se
    // choisit pas. Depuis l'espace élève, elle est la sortie vers l'espace parent : en dernier,
    // comme dans l'ancienne feuille, et elle presse.
    const parentRow = (
        <ProfileRow
            name={parentLabel}
            avatarSource={parentAvatarSource}
            active={parentActive}
            dimmed={!parentActive && busy}
            onPress={
                parentActive || busy || onSelectParent == null
                    ? undefined
                    : () => {
                          onClose()
                          onSelectParent()
                      }
            }
        />
    )

    // Avec `me`, l'élève est la tête de la feuille : sa ligne ne se répète pas dans la liste.
    const listed = me != null ? profiles.filter((profile) => profile.id !== selectedProfileId) : profiles

    // Personne vers qui basculer ni d'espace parent où aller : la section n'a pas de titre vide.
    const hasProfileRows = listed.length > 0 || parentActive || onSelectParent != null

    const profileList = (
        <>
            {parentActive && parentRow}
            {listed.map((profile) => {
                const isSelected = profile.id === selectedProfileId
                const openSettings = isSelected && onOpenAccountSettings != null
                return (
                    <ProfileRow
                        key={profile.id}
                        name={profile.firstname}
                        avatarUri={profile.avatarUrl}
                        active={isSelected}
                        dimmed={!isSelected && busy && switchingProfileId !== profile.id}
                        loading={switchingProfileId === profile.id}
                        settingsLabel={openSettings ? "Mon compte" : undefined}
                        onPress={
                            busy
                                ? undefined
                                : () => {
                                      onClose()
                                      if (openSettings) onOpenAccountSettings()
                                      else onSelectProfile?.(profile)
                                  }
                        }
                    />
                )
            })}
            {!parentActive && onSelectParent != null && parentRow}
        </>
    )

    // Sans version, le bouton est le pied à lui seul, comme avant qu'elle existe.
    const signOut =
        onSwitchAccount != null ? (
            <Button
                label="Se déconnecter"
                variant="outline"
                iconName="sign-out-alt"
                fullWidth
                loading={switchingAccount}
                disabled={switching}
                onPress={onSwitchAccount}
            />
        ) : undefined

    return (
        <Modal
            visible={visible}
            onClose={onClose}
            title={me != null ? "Mon espace" : "Changer de profil"}
            fullScreenOnSmall
            sizeToContent
            dismissable
            footer={
                signOut != null && version != null ? (
                    <Stack gap="2">
                        {signOut}
                        <Text size="text-sm" color="text-muted" center>
                            {`Version ${version}`}
                        </Text>
                    </Stack>
                ) : (
                    signOut
                )
            }
            testID={testID}
        >
            <Stack gap="1">
                {me == null ? (
                    profileList
                ) : (
                    <>
                        <MeRow
                            me={me}
                            avatarVariant={meAvatarVariant}
                            dimmed={busy}
                            onPress={
                                busy || onOpenAccountSettings == null
                                    ? undefined
                                    : () => {
                                          onClose()
                                          onOpenAccountSettings()
                                      }
                            }
                        />
                        {links.map((link) => (
                            <SettingRow
                                key={link.key}
                                iconName={link.iconName}
                                label={link.label}
                                loading={link.loading}
                                trailing={
                                    link.external === true && link.loading !== true ? (
                                        <Icon name="external-link-alt" size={16} color="text-muted" />
                                    ) : undefined
                                }
                                chevron={link.external === true ? false : undefined}
                                onPress={
                                    busy
                                        ? undefined
                                        : () => {
                                              if (link.loading) return
                                              if (link.keepOpen !== true) onClose()
                                              link.onPress()
                                          }
                                }
                                testID={testID != null ? `${testID}-link-${link.key}` : undefined}
                            />
                        ))}
                        {/* Sans famille connue sur l'appareil, il n'y a personne vers qui basculer. */}
                        {profiles.length > 0 && hasProfileRows && (
                            <>
                                <View style={styles.sectionLabel}>
                                    <SectionLabel>Changer de profil</SectionLabel>
                                </View>
                                {profileList}
                            </>
                        )}
                    </>
                )}
                {/* Pas de `busy` : l'apparence ne touche pas au compte, elle reste utilisable pendant une bascule. */}
                {appearance != null && (
                    <AppearanceSection
                        value={appearance.value}
                        onChange={appearance.onChange}
                        testID={testID != null ? `${testID}-appearance` : undefined}
                    />
                )}
            </Stack>
        </Modal>
    )
}

type MeRowProps = {
    me: UIProfileSwitcherMe
    avatarVariant: "signed" | "tutor"
    dimmed: boolean
    onPress?: () => void
}

/**
 * La tête « moi » : l'élève, son titre, et « Mon compte › ». Toute la ligne presse ; sans geste
 * (pendant une bascule, ou sans « Mon compte »), elle présente l'élève, sans le lien.
 */
function MeRow(props: MeRowProps) {
    const { me, avatarVariant, dimmed, onPress } = props
    const { name, avatarUri, title, notes = NO_NOTES } = me

    return (
        <Pressable
            onPress={onPress}
            disabled={onPress == null}
            accessibilityRole={onPress != null ? "button" : undefined}
            accessibilityLabel={onPress != null ? `Mon compte, ${name}` : undefined}
            style={styles.row(true, dimmed)}
        >
            <Row gap="3" align="center">
                {avatarVariant === "tutor" ? (
                    <Avatar uri={avatarUri} name={name} size={ME_AVATAR_SIZE} variant="tutor" />
                ) : (
                    <Avatar uri={avatarUri} name={name} size={ME_AVATAR_SIZE} signed />
                )}
                <Stack gap="0" style={styles.body}>
                    <Text size="text-xl" weight="bold" color="primary" numberOfLines={1}>
                        {name}
                    </Text>
                    {title != null && title !== "" && (
                        <Text size="text-sm" color="text-muted" numberOfLines={1}>
                            {title}
                        </Text>
                    )}
                    {notes.map((note) => (
                        <Text key={note} size="text-sm" color="text-muted">
                            {note}
                        </Text>
                    ))}
                </Stack>
                {onPress != null && (
                    <Row gap="1" align="center">
                        <Text size="text-sm" weight="bold" color="text-link">
                            Mon compte
                        </Text>
                        <Icon name="chevron-right" size={16} color="text-link" />
                    </Row>
                )}
            </Row>
        </Pressable>
    )
}

type ProfileRowProps = {
    name: string
    avatarSource?: number
    avatarUri?: string
    active: boolean
    dimmed: boolean
    loading?: boolean
    settingsLabel?: string
    onPress?: () => void
}

function ProfileRow(props: ProfileRowProps) {
    const { name, avatarSource, avatarUri, active, dimmed, loading = false, settingsLabel, onPress } = props

    return (
        <Pressable onPress={onPress} disabled={onPress == null} style={styles.row(active, dimmed)}>
            <Row gap="3">
                <Avatar source={avatarSource} uri={avatarUri} name={name} size={ROW_AVATAR_SIZE} selected={active} />
                <Stack gap="0" style={styles.body}>
                    <Text size="text-md" weight="semibold" color="primary">
                        {name}
                    </Text>
                    {settingsLabel != null && (
                        <Text size="text-xs" weight="bold" color="primary" style={styles.settings}>
                            {settingsLabel}
                        </Text>
                    )}
                </Stack>
                {loading ? (
                    <Loader size="small" color="primary" />
                ) : (
                    active && (
                        <Row gap="1">
                            <Text size="text-xs" weight="bold" color="primary">
                                Actif
                            </Text>
                            <Icon name="check" size={12} color="primary" />
                        </Row>
                    )
                )}
            </Row>
        </Pressable>
    )
}

const styles = StyleSheet.create((theme) => ({
    row: (active: boolean, dimmed: boolean) => ({
        padding: theme.spacing["3"],
        borderRadius: theme.radius.md,
        backgroundColor: active ? theme.colors["primary-soft"] : "transparent",
        // Pendant une bascule, les profils qu'on ne rejoint pas s'effacent : le legacy pose la
        // même opacité, et c'est elle qui dit « ça travaille, sur celui-là ».
        opacity: dimmed ? 0.5 : 1,
    }),
    body: { flexShrink: 1, flexGrow: 1 },
    settings: { textDecorationLine: "underline" as const },
    // Comme « Apparence » : aligné sur le bord des avatars, l'écart du dessus sépare la section de
    // la tête sans filet.
    sectionLabel: {
        paddingHorizontal: theme.spacing["3"],
        paddingTop: theme.spacing["4"],
    },
}))
